/**
 * Subscription tier resolver — deterministic, monetization-blind.
 *
 * Doctrine alignment:
 * - MONETIZATION_ARCHITECTURE.md: "Payment never changes competitive outcomes."
 * - MASTER_OPERATING_MODEL.md Layer 3: scoring/matchmaking have no input for payment status.
 *
 * This module resolves feature access from profile billing columns only.
 * It MUST NOT import or reference any competitive engine module.
 */

import type { Profile, SubscriptionTierV1 } from '@/types/database';
import { createLogger } from '@/lib/logger';

const log = createLogger('SUB');

export type Tier = SubscriptionTierV1;

// ── Tier normalization ────────────────────────────────────────

const TIER_RANK: Record<Tier, number> = {
  free: 0,
  pro: 1,
  coach_pro: 2,
  org_pro: 3,
};

/**
 * Determine the effective tier for a profile.
 * If subscription_expires_at is in the past, falls back to 'free'.
 */
const VALID_TIERS: ReadonlySet<string> = new Set(['free', 'pro', 'coach_pro', 'org_pro']);

/**
 * club_* product IDs from RevenueCat map to org_pro entitlement.
 * These are the 4 club tier product IDs from club-onboarding.tsx.
 * They are NOT separate SubscriptionTierV1 values — they normalize to org_pro
 * so PaywallGate and useSubscription work without enum changes.
 * Member limits and coach seats are enforced via getClubMemberLimit/getClubCoachSeats.
 */
const CLUB_TIER_TO_ORG_PRO: ReadonlySet<string> = new Set([
  'club_starter',
  'club_growth',
  'club_performance',
  'club_elite',
]);

export function normalizeTier(profile: Pick<Profile, 'subscription_tier' | 'subscription_expires_at'> | null | undefined): Tier {
  if (!profile?.subscription_tier) return 'free';
  const raw = profile.subscription_tier;
  if (raw === 'free') return 'free';

  // Map all club_* product IDs to org_pro entitlement level
  if (CLUB_TIER_TO_ORG_PRO.has(raw)) {
    // Still check expiry before granting org_pro
    if (profile.subscription_expires_at) {
      const expires = new Date(profile.subscription_expires_at);
      const now = new Date();
      const expiresEndOfDay = new Date(expires.getFullYear(), expires.getMonth(), expires.getDate(), 23, 59, 59, 999);
      const nowStartOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      if (expiresEndOfDay.getTime() < nowStartOfDay.getTime()) {
        log.info('[Subscription] club tier expired, treating as free', { rawTier: raw });
        return 'free';
      }
    }
    log.info('[Subscription] club tier mapped to org_pro', { rawTier: raw });
    return 'org_pro';
  }

  // Reject unexpected tier values
  if (!VALID_TIERS.has(raw)) {
    log.warn('[Subscription] unexpected tier value, treating as free:', raw);
    return 'free';
  }

  // Expired subscription → free
  if (profile.subscription_expires_at) {
    const expires = new Date(profile.subscription_expires_at);
    const now = new Date();
    
    // Set both dates to the end of their respective days for proper comparison
    // This ensures subscription is valid until the END of the expiration date
    const expiresEndOfDay = new Date(expires.getFullYear(), expires.getMonth(), expires.getDate(), 23, 59, 59, 999);
    const nowStartOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    if (expiresEndOfDay.getTime() < nowStartOfDay.getTime()) {
      log.info('[Subscription] tier expired', {
        rawTier: raw,
        expiresAt: profile.subscription_expires_at,
        expiresEndOfDay: expiresEndOfDay.toISOString(),
        nowStartOfDay: nowStartOfDay.toISOString(),
      });
      return 'free';
    }
  }

  return raw as Tier;
}

/**
 * Numeric rank for tier comparison. Higher = more access.
 */
export function tierRank(tier: Tier): number {
  return TIER_RANK[tier] ?? 0;
}

/**
 * Returns true if `tier` is at least `required` in the hierarchy.
 */
export function hasAtLeast(tier: Tier, required: Tier): boolean {
  return tierRank(tier) >= tierRank(required);
}

// ── Capability resolver ───────────────────────────────────────

/** Club sub-tier based on plan ID stored in subscription_plan_id on profile */
export type ClubSubTier = 'starter' | 'growth' | 'performance' | 'elite' | null;

export const CLUB_MEMBER_LIMITS: Record<string, number> = {
  club_starter: 50,
  club_growth: 150,
  club_performance: 500,
  club_elite: Infinity,
};

export const CLUB_COACH_SEATS: Record<string, number> = {
  club_starter: 2,
  club_growth: 5,
  club_performance: 10,
  club_elite: Infinity,
};

export function getClubSubTier(planId: string | null | undefined): ClubSubTier {
  if (!planId) return null;
  if (planId.includes('starter')) return 'starter';
  if (planId.includes('growth')) return 'growth';
  if (planId.includes('performance')) return 'performance';
  if (planId.includes('elite')) return 'elite';
  return null;
}

export function getClubMemberLimit(planId: string | null | undefined): number {
  if (!planId) return 50; // default to starter limits
  return CLUB_MEMBER_LIMITS[planId] ?? 50;
}

export function getClubCoachSeats(planId: string | null | undefined): number {
  if (!planId) return 2;
  return CLUB_COACH_SEATS[planId] ?? 2;
}

export interface Capabilities {
  isFree: boolean;
  isPro: boolean;
  isCoachPro: boolean;
  isOrgPro: boolean;
  canAdvancedAnalytics: boolean;
  canCoachTools: boolean;
  canOrgTools: boolean;
  canExportPersonal: boolean;
  canExportOrgAggregates: boolean;
}

/**
 * Resolve boolean capabilities from a tier.
 *
 * Mapping (cumulative):
 * - pro: advanced analytics + personal export
 * - coach_pro: + coach tools
 * - org_pro: + org tools + org aggregate export
 */
// ── Feature → tier mapping ────────────────────────────────────

const FEATURE_TIER: Record<string, Tier> = {
  advanced_analytics: 'pro',
  workout_templates: 'pro',
  personal_export: 'pro',
  coach_tools: 'coach_pro',
  program_builder: 'coach_pro',
  org_tools: 'org_pro',
  org_export: 'org_pro',
};

/**
 * Returns true if the user should be shown the paywall for a given feature.
 */
export function shouldShowPaywall(tier: Tier, feature: string): boolean {
  const required = FEATURE_TIER[feature];
  if (!required) return false; // unknown feature → no paywall
  return !hasAtLeast(tier, required);
}

export function resolveCapabilities(tier: Tier): Capabilities {
  const rank = tierRank(tier);
  return {
    isFree: tier === 'free',
    isPro: tier === 'pro',
    isCoachPro: tier === 'coach_pro',
    isOrgPro: tier === 'org_pro',
    canAdvancedAnalytics: rank >= TIER_RANK.pro,
    canCoachTools: rank >= TIER_RANK.coach_pro,
    canOrgTools: rank >= TIER_RANK.org_pro,
    canExportPersonal: rank >= TIER_RANK.pro,
    canExportOrgAggregates: rank >= TIER_RANK.org_pro,
  };
}
